import { useState, useCallback } from "react";
import apiClient from "../api/client";
import { config } from "../config";

export const useSecrets = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSecrets = useCallback(async (platformName, headers) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/${platformName}/secrets`, { headers });
      const secretsWithIds = response.data.map((secret, index) => ({
        ...secret,
        id: index + 1,
      }));

      // Normalize tag format for non-AWS platforms
      const normalizedSecrets = secretsWithIds.map((secret) => {
        if (platformName !== config.platforms[0]) {
          const formattedTags = Object.entries(secret.tags || {}).map(
            ([key, value]) => ({ Key: key, Value: value })
          );
          return { ...secret, tags: formattedTags };
        }
        return secret;
      });

      return normalizedSecrets;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSecret = useCallback(
    async (secretName, platformName, headers, version = "current") => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/${platformName}/secrets/${secretName}`,
          { headers: { ...headers, Version: version } }
        );
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Fetch full version history for a Vault secret.
   * Returns { versions: [...], current_version: N }
   */
  const fetchVersionHistory = useCallback(
    async (secretName, platformName, headers) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/${platformName}/secrets/${secretName}/versions`,
          { headers }
        );
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Fetch recent viewers of a secret. Best-effort/informational only — see
   * backend/access_log.py for real limitations (in-memory, per-process,
   * self-reported identity). Doesn't toggle the shared `loading` flag since
   * it's a secondary fetch, not something that should block the main view.
   */
  const fetchAccessLog = useCallback(async (secretName, platformName, headers) => {
    const response = await apiClient.get(
      `/${platformName}/secrets/${secretName}/access-log`,
      { headers }
    );
    return response.data;
  }, []);

  const createSecret = useCallback(
    async (secretData, platformName, headers) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.post(
          `/${platformName}/secrets`,
          secretData,
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateSecret = useCallback(
    async (secretName, secretData, platformName, headers) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.put(
          `/${platformName}/secrets/${secretName}`,
          secretData,
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteSecret = useCallback(
    async (secretName, platformName, headers) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.delete(
          `/${platformName}/secrets/${secretName}`,
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Rollback to a specific version (or previous if target_version not supplied).
   * For Vault: POST /vault/secrets/:name/rollback  { target_version: N }
   * For AWS/AzKV: POST /platform/secrets/:name (existing behaviour)
   */
  const rollbackSecret = useCallback(
    async (secretName, platformName, headers, targetVersion = null) => {
      setLoading(true);
      setError(null);
      try {
        const url =
          platformName === "vault"
            ? `/${platformName}/secrets/${secretName}/rollback`
            : `/${platformName}/secrets/${secretName}`;

        const body = targetVersion !== null ? { target_version: targetVersion } : {};

        const response = await apiClient.post(url, body, { headers });
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    fetchSecrets,
    fetchSecret,
    fetchVersionHistory,
    fetchAccessLog,
    createSecret,
    updateSecret,
    deleteSecret,
    rollbackSecret,
  };
};